import styled from "@emotion/styled";
import { ArrowRight } from "@mui/icons-material";
import {
  Breadcrumbs,
  Button,
  Checkbox,
  Stack,
  Typography
} from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { useChurchStore } from "features/churches";
import { capitalize, startCase } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useAuthUser } from "react-auth-kit";
import { useNavigate, useParams } from "react-router-dom";
import {
  useApproveRequisition,
  useRejectRequisition,
  useRequisition
} from "../api";
import { RequisitionRequestForm } from "../components/Forms/RequisitionRequestForm";
import { statusColors, statusStyleTypes } from "../constants/statuses";

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
  cursor: pointer;
`;

const Status = styled.div`
  border-radius: 2px;
  background: ${({ color }) => color};
  color: #ffffff;
  padding: 4px 8px;
  text-transform: capitalize;
  font-size: 10px;
`;

const Textarea = styled.textarea`
  resize: none;
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  outline: none;
  padding: 8px;
  font-family: inherit;
  align-self: flex-start;
  width: 900px;
`;

const StyledAlert = styled.div`
  padding: 16px;
  background-color: ${props => props.bg};
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: 20px;
  flex: 1;

  .startIcon {
    min-height: 48px;
    min-width: 48px;
    background-color: ${props => props.iconBg};
    display: grid;
    place-items: center;
    font-size: 24px;
    border-radius: 100%;
    color: #ffffff;
  }

  div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .endIcon {
    min-height: 24px;
    min-width: 24px;
    background-color: #ffffff;
    display: grid;
    place-items: center;
    border-radius: 100%;
    margin-left: auto;
    cursor: pointer;
  }

  h3 {
    margin: 0;
  }

  p {
    margin: 0;
  }
`;

export const RequisitionApproval = () => {
  const [requisition, setRequisition] = useState(null);
  const [reason, setReason] = useState("");
  const [isRejected, setIsRejected] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const auth = useAuthUser();
  const userId = auth()?.id;

  const rejectRequisitionMutation = useRejectRequisition();
  const approveRequisitionMutation = useApproveRequisition();

  const isApprover = useMemo(
    () =>
      requisition?.approvers?.some(approver => approver.approver_id === userId),
    [requisition, userId]
  );

  const { data, isLoading } = useRequisition({
    churchId: selectedChurch?.id,
    id
  });

  useEffect(() => {
    if (data) {
      setRequisition(data.result);
    }
  }, [data]);

  const rejectRequisition = () => {
    rejectRequisitionMutation.mutate(
      {
        churchId: selectedChurch?.id,
        requisitionData: { reason_for_rejection: reason },
        requisitionId: id
      },
      {
        onSuccess: () => {
          navigate(`/finances/temp/expense/requisition`);
        }
      }
    );
  };

  const approveRequisition = () => {
    approveRequisitionMutation.mutate(
      {
        churchId: selectedChurch?.id,
        requisitionData: {},
        requisitionId: id
      },
      {
        onSuccess: () => {
          navigate(`/finances/temp/expense/requisition`);
        }
      }
    );
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Stack spacing={3}>
      <Breadcrumbs
        separator={<ArrowRight fontSize="small" />}
        aria-label="breadcrumb"
      >
        <Title onClick={() => navigate("/finances/temp/expense")}>
          Expense
        </Title>
        <Title onClick={() => navigate("/finances/temp/expense/requisition")}>
          Requisition
        </Title>
        <Title bold>Request</Title>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" spacing={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            backgroundColor: "#FAFAFA",
            borderRadius: 1,
            p: 2,
            width: "900px"
          }}
        >
          <Stack>
            <Typography variant="caption">Status</Typography>
            <Status color={statusColors[requisition?.requisition_status]}>
              {startCase(capitalize(requisition?.requisition_status))}
            </Status>
          </Stack>
          <Stack>
            <Typography variant="caption">Available Budget</Typography>
            <Typography fontWeight="bold">
              {Number(requisition?.amount).toLocaleString()} ETB
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="caption">Current Approver</Typography>
            <Stack>
              {requisition?.approvers?.map(approver => (
                <Stack direction="row" alignItems="center" key={approver.id}>
                  <Typography fontWeight="bold">
                    {approver.approver.first_name} {approver.approver.last_name}
                  </Typography>
                  <Checkbox
                    checked={approver.pivot.status === "APPROVED"}
                    readOnly
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
        {(requisition?.requisition_status === "DRAFTED" ||
          requisition?.requisition_status === "SCHEDULED") && (
          <StyledAlert
            bg={
              statusStyleTypes[
                requisition?.requisition_status?.toLowerCase()
              ]?.["backgroundColor"]
            }
            iconBg={
              statusStyleTypes[
                requisition?.requisition_status?.toLowerCase()
              ]?.["iconBackgroundColor"]
            }
          >
            <span className="startIcon">
              {
                statusStyleTypes[
                  requisition?.requisition_status?.toLowerCase()
                ]?.["icon"]
              }
            </span>
            <div>
              <h3>
                {
                  statusStyleTypes[
                    requisition?.requisition_status?.toLowerCase()
                  ]?.["title"]
                }
              </h3>
              <p>
                {requisition?.requisition_status?.toLowerCase() === "scheduled"
                  ? `This payment is scheduled to be paid on ${moment(
                      requisition?.schedule_at
                    ).format("DD/MM/YYYY")}.`
                  : statusStyleTypes[
                      requisition?.requisition_status?.toLowerCase()
                    ]?.["message"]}
              </p>
            </div>
          </StyledAlert>
        )}
      </Stack>

      {requisition && (
        <RequisitionRequestForm
          isView={true}
          isEditMode={false}
          data={requisition}
        />
      )}

      {isRejected && (
        <Textarea
          placeholder="Reason for rejection"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={4}
        />
      )}

      {isRejected && (
        <Stack direction="row" justifyContent="flex-start" spacing={2}>
          <Button
            variant="contained"
            onClick={rejectRequisition}
            disabled={!reason || rejectRequisitionMutation.isLoading}
          >
            Send
          </Button>
          <Button
            variant="outlined"
            onClick={() => setIsRejected(false)}
            disabled={rejectRequisitionMutation.isLoading}
          >
            Cancel
          </Button>
        </Stack>
      )}

      {isApprover && !isRejected && (
        <Stack direction="row" justifyContent="flex-start" spacing={2}>
          <Button
            variant="contained"
            color="success"
            onClick={approveRequisition}
            disabled={approveRequisitionMutation.isLoading}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => setIsRejected(true)}
            disabled={approveRequisitionMutation.isLoading}
          >
            Reject
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
